'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {
    InputNumber,
    InputNumberProps,
    InputNumberChangeEvent
} from 'primereact/inputnumber';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    convertToInteger,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {integerRenderer} from '~/shared/src/utilities/valueRenderers';
import {StoreState} from '~/frontend/src/store';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';


type InputProps = Omit<InputNumberProps, 'onChange' | 'key'>;
const inputProps = keys<InputProps>();

type InputChangeProps = InputNumberChangeEvent;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The unit of the value
     */
    valueUnit? : string;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    valueUnit      : '',
    rawMode        : false,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : number | '-';

    /**
     * The value to determine dirty against.
     */
    originalValue? : number;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : number | null, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : number | null, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const IntegerField = React.forwardRef<InputNumber, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  autoComplete,
                  disabled,
                  displayValue,
                  id,
                  inputClassNames,
                  inputStyle,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  pt,
                  rawMode,
                  useHookForm,
                  value,
                  valueUnit
              }                                 = props,
              elementId                         = id ?? `${uid}_integerField`,
              inValue                           = useRef<number | null>( value ?? null ),
              outValue                          = useRef<number | '-' | null>( value ?? null ),
              timeoutId                         = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ] = useState<number | '-' | null>( value ?? null ),
              [ focus, setFocus ]               = useState<boolean>( false ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder               = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              fieldWrapperAutoCompleteConfig    = useMemo(
                  () => {
                      let retVal : Partial<FieldWrapperComponentProps> = {};

                      if ( autoComplete && !disabled ) {
                          retVal = {
                              autoCompleteValue                  : elementValue !== undefined ? convertValueToString( elementValue, 'INTEGER' ) : '',
                              autoCompleteInputHasFocus          : focus,
                              onAutoCompleteValueChange          : ( autoCompleteValueRaw : string, originalEvent : any ) => {
                                  const autoCompleteValue = convertToInteger( autoCompleteValueRaw ) as number;

                                  if ( !isNaN( autoCompleteValue ) && autoCompleteValue !== elementValue ) {
                                      setElementValue( autoCompleteValue );

                                      onChange( autoCompleteValue, originalEvent );
                                  }
                              },
                              autoCompleteSuggestionsItemTemplate: ( suggestion : string ) => {
                                  return integerRenderer( suggestion, valueUnit, rawMode );
                              }
                          };
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementValue,
                      focus,
                      onChange
                  ]
              ),
              onInputChange                     = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange             = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onInputFocus                      = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                       = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current === '-' ) {
                          onChange( null, event )
                      }
                      else if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              fieldWrapperDisplayValue          = useMemo(
                  () => {
                      let retVal : React.ReactNode | undefined;

                      if ( displayValue !== undefined ) {
                          retVal = displayValue;
                      }
                      else if ( !rawMode && value !== undefined && ( value as number | '-' ) !== '-' && valueUnit ) {
                          retVal = integerRenderer( value, valueUnit, rawMode );
                      }

                      return retVal;
                  },
                  [
                      displayValue,
                      rawMode,
                      value,
                      valueUnit
                  ]
              );

        inValue.current = value ?? null;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value ?? null );
                }
            },
            [
                value
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
            displayValue={fieldWrapperDisplayValue}

            {...fieldWrapperAutoCompleteConfig}
            pt={pt?.fieldWrapper}
        >
            <InputNumber
                {..._.pick( props, inputProps ) as InputProps}

                id={elementId}

                ref={forwardedRef}

                // PrimeReact type definition lacks of '-'
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                value={elementValue}

                onChange={useHookForm ? onHookFormInputChange : onInputChange}

                pt={{
                    input: {
                        root: {
                            onFocus: onInputFocus,
                            onBlur : onInputBlur
                        }
                    }
                }}

                useGrouping={!rawMode}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={0}

                className={`${focus ? 'p-focus' : ''}`}
                inputClassName={`yn-integerfield ${inputClassNames.join( ' ' )}`}
                style={{ ...defaultProps.inputStyle, ...inputStyle }}

                placeholder={resolvedPlaceholder}
                tooltip={undefined}
                tooltipOptions={undefined}

                aria-describedby={`${elementId}_message`}

                aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
            />
        </FieldWrapper>;
    }
} );
IntegerField.defaultProps = defaultProps;
IntegerField.displayName = 'IntegerField';

export default IntegerField;
